import { api } from '@/shared/lib/api'
import type { User } from '@workspace/lib/api'
import { UserStatus } from '@workspace/lib/api'
import { DeleteUserConfirmDialog } from '@/shared/components/dialogs/DeleteUserConfirmDialog'
import { FilterDropdown } from '@/shared/components/FilterDropdown'
import TabLayout, { ActionButton } from '@/shared/layouts/TabLayout'
import { AddUser } from '@/shared/components/user/AddUser'
import { EditUser } from '@/shared/components/user/EditUser'
import { UserDetail } from '@/shared/components/user/UserDetail'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { createColumnHelper } from '@tanstack/react-table'
import { Button } from '@workspace/ui/components/Button'
import { DataTable, type DataTableSorting } from '@workspace/ui/components/DataTable'
import { Menu, MenuHeader, MenuItem, MenuPopover, MenuTrigger } from '@workspace/ui/components/Menu'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import { toast } from '@workspace/ui/components/Sonner'
import dayjs from 'dayjs'
import { MoreHorizontal, PlusIcon, ShieldCheck } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TableDataFooter from '@/shared/components/TableDataFooter'
import { UserStatusBadge } from '@/shared/components/user/UserStatusBadge'
import { cn } from '@workspace/ui/lib/utils'
import { QuickPermissionDialog } from '@/shared/components/user/QuickPermissionDialog'

const searchSchema = z.object({
    q: z.string().optional(),
    page: z.number().catch(1).optional(),
    pageSize: z.number().catch(10).optional(),
    roles: z.string().optional(),
    statuses: z.string().optional(),
    regions: z.string().optional(),
    areas: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.string().optional(),
})

export const Route = createFileRoute('/users/')({
    validateSearch: searchSchema,
    component: AdminUsers,
})

const columnHelper = createColumnHelper<User>()

function AdminUsers() {
    const { t } = useTranslation()
    const searchParams = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }) })
    }

    const currentPage = searchParams.page || 1
    const pageSize = searchParams.pageSize || 10
    const searchName = searchParams.q || ''
    const roleValues = searchParams.roles ? searchParams.roles.split(',') : []
    const statusValues = searchParams.statuses ? searchParams.statuses.split(',') : []
    const regionValues = searchParams.regions ? searchParams.regions.split(',') : []
    const areaValues = searchParams.areas ? searchParams.areas.split(',') : []
    const sorting = searchParams.sortBy
        ? { sortBy: searchParams.sortBy, sortDirection: searchParams.sortDirection as 'asc' | 'desc' }
        : { sortBy: 'createdAt', sortDirection: 'desc' }

    const setCurrentPage = (page: number) => updateSearch({ page })
    const setPageSize = (pageSize: number) => updateSearch({ pageSize, page: 1 })
    const setSearchName = (q: string) => updateSearch({ q, page: 1 })
    const setRoleValues = (roles: string[]) =>
        updateSearch({ roles: roles.length > 0 ? roles.join(',') : undefined, page: 1 })
    const setStatusValues = (statuses: string[]) =>
        updateSearch({ statuses: statuses.length > 0 ? statuses.join(',') : undefined, page: 1 })
    const setRegionValues = (regions: string[]) => {
        updateSearch({ regions: regions.length > 0 ? regions.join(',') : undefined, page: 1 })
    }
    const setAreaValues = (areas: string[]) =>
        updateSearch({ areas: areas.length > 0 ? areas.join(',') : undefined, page: 1 })
    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.sortBy, sortDirection: sort.sortDirection, page: 1 })
        }
    }

    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
    const [openCreateUser, setOpenCreateUser] = useState(false)
    const [openQuickPermission, setOpenQuickPermission] = useState(false)
    const [userAction, setUserAction] = useState<{ user: User; action: 'view' | 'edit' | 'delete' } | null>(null)

    const debouncedSearch = useDebounce(searchName, 300)

    const { data: regionsData } = useQuery({
        queryKey: ['regions'],
        queryFn: () => api.region.list({ page: 1, take: 100 }),
    })

    const deleteMutation = useMutation({
        mutationFn: async (userIds: string[]) => {
            // Delete users sequentially
            for (const id of userIds) {
                await api.user.softDelete(id)
            }
            return userIds
        },
        onSuccess: () => {
            toast.success({
                title: t('common.status.success'),
                description: t('users.list.deleteSuccess.description'),
            })
            refetch()
            setRowSelection({})
        },
        onError: error => {
            toast.error({
                title: t('common.status.error'),
                description: t('users.list.deleteError.description'),
            })
            console.error('Delete error:', error)
        },
    })

    const reInviteMutation = useMutation({
        mutationFn: async (id: string) => {
            return api.user.reInvite(id)
        },
        onSuccess: () => {
            toast.success({
                title: t('common.status.success'),
                description: t('users.list.reInviteSuccess.description'),
            })
            refetch()
        },
        onError: error => {
            console.error('Re-invite error:', error)
        },
    })

    const { data: areasData } = useQuery({
        queryKey: ['areas'],
        queryFn: () => api.area.list({ page: 1, take: 100 }),
    })

    const { data: rolesData } = useQuery({
        queryKey: ['roles'],
        queryFn: () => api.role.list({ page: 1, take: 100 }),
    })

    const {
        data: rawData,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: [
            'users',
            debouncedSearch,
            roleValues,
            statusValues,
            regionValues,
            areaValues,
            currentPage,
            pageSize,
            sorting,
        ],
        queryFn: async () => {
            const isInviteFilterActive =
                statusValues.length > 0 &&
                (statusValues.includes('INVITED') || statusValues.includes('INVITATION_EXPIRED'))

            // Map invited/expired to backend PENDING status
            const backendStatuses = statusValues.map(status =>
                status === 'INVITED' || status === 'INVITATION_EXPIRED' ? 'PENDING' : status,
            )
            const uniqueStatuses = [...new Set(backendStatuses)]

            const baseParams = {
                q: debouncedSearch || undefined,
                roleIds: roleValues.length > 0 ? roleValues.join(',') : undefined,
                statuses: uniqueStatuses.length > 0 ? uniqueStatuses.join(',') : undefined,
                regionIds: regionValues.length > 0 ? regionValues.join(',') : undefined,
                areaIds: areaValues.length > 0 ? areaValues.join(',') : undefined,
                sort: sorting?.sortBy === 'fullName' ? 'name' : sorting?.sortBy,
                order: sorting?.sortDirection?.toUpperCase() as 'ASC' | 'DESC' | undefined,
            }

            // If invite-related filtering is active, backend can't distinguish INVITED vs INVITATION_EXPIRED.
            // Fallback: fetch all pages of the backend result for the mapped statuses, then let client-side
            // logic separate INVITED vs INVITATION_EXPIRED and paginate correctly.
            if (isInviteFilterActive) {
                const perPage = 100
                let page = 1
                let allItems: User[] = []
                let lastMeta: any = undefined
                const maxPages = 50 // safety limit to avoid infinite loops

                while (page <= maxPages) {
                    // eslint-disable-next-line no-await-in-loop
                    const resp = await api.user.list({ page, take: perPage, ...baseParams })
                    allItems = allItems.concat(resp.data)
                    lastMeta = resp.meta
                    if (!resp.data || resp.data.length < perPage) break
                    page += 1
                }

                return {
                    data: allItems,
                    meta: {
                        ...(lastMeta || {}),
                        // meta.page and meta.take represent what we will render, but
                        // the client will compute the effective itemCount after filtering.
                        page: currentPage,
                        take: pageSize,
                        itemCount: allItems.length,
                    },
                }
            }

            return api.user.list({
                page: currentPage,
                take: pageSize,
                ...baseParams,
            })
        },
    })

    const data = useMemo(() => {
        if (!rawData) return rawData

        const isInviteFilterActive =
            statusValues.length > 0 && (statusValues.includes('INVITED') || statusValues.includes('INVITATION_EXPIRED'))

        if (!isInviteFilterActive) {
            return rawData
        }

        const filteredData = rawData.data.filter(user => {
            if ((user.status as string) !== 'PENDING') return true

            const isExpired = user.confirmationTokenExpired && new Date(user.confirmationTokenExpired) < new Date()

            if (statusValues.includes('INVITED') && !statusValues.includes('INVITATION_EXPIRED')) {
                return !isExpired
            }

            if (statusValues.includes('INVITATION_EXPIRED') && !statusValues.includes('INVITED')) {
                return isExpired
            }

            // If both selected, include all PENDING users
            return true
        })

        const totalFiltered = filteredData.length
        const start = (currentPage - 1) * pageSize
        const end = start + pageSize
        const paginated = filteredData.slice(start, end)

        return {
            ...rawData,
            data: paginated,
            meta: {
                ...rawData.meta,
                itemCount: totalFiltered,
            },
        }
    }, [rawData, statusValues, currentPage, pageSize])

    const regionOptions = useMemo(
        () => regionsData?.data.map(region => ({ id: region.id, name: region.name })) ?? [],
        [regionsData],
    )

    const areaOptions = useMemo(() => {
        const allAreas = areasData?.data || []
        if (regionValues.length === 0) return allAreas.map((area: any) => ({ id: area.id, name: area.name }))
        return allAreas
            .filter((area: any) => regionValues.includes(area.region?.id))
            .map((area: any) => ({ id: area.id, name: area.name }))
    }, [areasData, regionValues])

    const roleOptions = useMemo(
        () => rolesData?.data.map(role => ({ id: role.id, name: role.displayName })) ?? [],
        [rolesData],
    )

    // Auto-prune areaValues when regionValues change
    useEffect(() => {
        if (!areasData?.data || areaValues.length === 0) return

        if (regionValues.length === 0) {
            updateSearch({ areas: undefined })
            return
        }

        const validAreaIds = new Set(
            areasData.data.filter((a: any) => regionValues.includes(a.region?.id)).map((a: any) => a.id),
        )

        const newAreaValues = areaValues.filter(id => validAreaIds.has(id))

        if (newAreaValues.length !== areaValues.length) {
            updateSearch({
                areas: newAreaValues.length > 0 ? newAreaValues.join(',') : undefined,
                page: 1,
            })
        }
    }, [regionValues, areaValues, areasData])

    const statusOptions = useMemo(
        () => [
            { id: 'ACTIVE', name: 'Kích hoạt' },
            { id: 'INVITED', name: 'Đã mời' },
            { id: 'INVITATION_EXPIRED', name: 'Quá hạn mời' },
            { id: 'INACTIVE', name: 'Chưa kích hoạt' },
            { id: 'BLOCKED', name: 'Tạm khoá' },
        ],
        [],
    )

    const [isDeletingUser, setIsDeletingUser] = useState(false)

    const handleDelete = async () => {
        if (!userAction?.user) return

        try {
            setIsDeletingUser(true)
            await api.user.softDelete(userAction.user.id)
            toast.success({
                title: t('common.status.success'),
                description: t('users.list.deleteSuccess.description'),
            })
            refetch()
        } catch (error) {
            toast.error({
                title: t('common.status.error'),
                description: t('users.list.deleteError.description'),
            })
            console.error('Delete error:', error)
        } finally {
            setIsDeletingUser(false)
        }

        setUserAction(null)
    }

    const handleDeleteMultiple = async () => {
        const selectedIds = Object.keys(rowSelection).filter(id => rowSelection[id])
        if (selectedIds.length === 0) return

        await deleteMutation.mutateAsync(selectedIds)
    }

    const handleKeydown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (currentPage !== 1) setCurrentPage(1)
            else await refetch()
        }
    }

    const totalResults = data?.meta.itemCount || 0

    const columns = [
        columnHelper.display({
            id: 'custom-actions',
            header: '',
            size: 80,
            enableSorting: false,
            meta: { className: '!max-w-[46px] !min-w-[46px] !w-[46px]' },
            cell: ({ row }) => {
                const user = row.original
                const isPending = (user.status as string) === 'PENDING'
                const canReinvite =
                    (user.status as string) === 'INVITED' ||
                    (user.status as string) === 'INVITATION_EXPIRED' ||
                    isPending

                return (
                    <MenuTrigger>
                        <Button variant="outline" size="iconSm" className="rounded-full border-foreground h-5 w-5">
                            <MoreHorizontal className="size-3!" />
                        </Button>
                        <MenuPopover className="w-[120px]">
                            <Menu>
                                <MenuHeader className="text-foreground">{t('common.actions.operation')}</MenuHeader>
                                <MenuItem
                                    onAction={() => reInviteMutation.mutate(user.id)}
                                    className={cn('cursor-pointer', !canReinvite && 'hidden')}
                                >
                                    {t('common.actions.reInvite')}
                                </MenuItem>
                                <MenuItem
                                    onAction={() => setUserAction({ user, action: 'view' })}
                                    className="cursor-pointer"
                                >
                                    {t('common.actions.view')}
                                </MenuItem>
                                <MenuItem
                                    onAction={() => setUserAction({ user, action: 'edit' })}
                                    className="cursor-pointer"
                                >
                                    {t('common.actions.edit')}
                                </MenuItem>
                                <MenuItem
                                    onAction={() => setUserAction({ user, action: 'delete' })}
                                    className="cursor-pointer text-destructive"
                                >
                                    {t('common.actions.delete')}
                                </MenuItem>
                            </Menu>
                        </MenuPopover>
                    </MenuTrigger>
                )
            },
        }),
        columnHelper.accessor('fullName', {
            header: t('users.list.table.name'),
            meta: { headerClassName: 'font-bold' },
            size: 180,
            enableSorting: true,
            cell: ({ row }) => {
                const user = row.original
                const fullName = user.fullName
                const position = user.position || user?.role?.displayName || ''

                return (
                    <div
                        className="flex flex-col py-1 cursor-pointer"
                        onClick={() => setUserAction({ user, action: 'view' })}
                    >
                        <span className="text-sm font-medium text-foreground leading-5">{fullName || '-'}</span>
                        <span
                            className="text-xs text-left text-muted-foreground leading-4 whitespace-normal line-clamp-2"
                            title={position}
                        >
                            {position}
                        </span>
                    </div>
                )
            },
        }),
        columnHelper.display({
            id: 'region-area',
            header: t('users.list.table.area'),
            size: 150,
            enableSorting: false,
            meta: { headerClassName: 'font-bold', noTitleTooltip: true },
            cell: ({ row }) => {
                const region = row.original.region?.name
                const areaNames = row.original.areas?.map(a => a.name).filter(Boolean) || []
                const areaJoined = areaNames.join(', ')
                const display =
                    areaNames.length >= 3
                        ? `${areaNames.slice(0, 2).join(', ')} +${areaNames.length - 2}`
                        : areaJoined || '-'

                return (
                    <div className="flex flex-col py-1 max-w-full">
                        <span className="text-sm font-medium text-foreground leading-5 truncate" title={region}>
                            {region || '-'}
                        </span>
                        <span
                            className="text-xs text-muted-foreground leading-4 truncate"
                            title={areaNames.length >= 3 ? areaJoined : display}
                        >
                            {display}
                        </span>
                    </div>
                )
            },
        }),
        columnHelper.accessor('role.displayName', {
            header: t('users.list.table.role'),
            meta: { headerClassName: 'font-bold' },
            size: 120,
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'department',
            header: t('users.list.table.department'),
            meta: { headerClassName: 'font-bold' },
            size: 140,
            enableSorting: false,
            cell: ({ row }) => {
                const department = row.original.department
                return (
                    <span className="text-sm text-foreground" title={department?.name}>
                        {department?.name || '-'}
                    </span>
                )
            },
        }),
        columnHelper.accessor('email', {
            header: t('users.list.table.email'),
            meta: { headerClassName: 'font-bold' },
            size: 240,
            enableSorting: false,
        }),
        columnHelper.accessor('phone', {
            header: t('users.list.table.phone'),
            meta: { headerClassName: 'font-bold' },
            size: 120,
            enableSorting: false,
        }),
        columnHelper.accessor('status', {
            header: t('users.list.table.status'),
            meta: { headerClassName: 'font-bold' },
            size: 130,
            enableSorting: false,
            cell: ({ row }) => (
                <UserStatusBadge
                    status={row.original.status}
                    confirmationTokenExpired={row.original.confirmationTokenExpired}
                />
            ),
        }),
        columnHelper.accessor('createdAt', {
            header: t('users.list.table.createdAt'),
            meta: { headerClassName: 'font-bold' },
            size: 140,
            enableSorting: true,
            cell: ({ getValue }) => {
                const value = dayjs(getValue() as string).format('DD/MM/YYYY HH:mm')
                return (
                    <span className="text-sm text-foreground" title={value}>
                        {value ?? '-'}
                    </span>
                )
            },
        }),
    ]

    const links = [
        { to: '/users', label: t('tabs.users') },
        { to: '/users/roles', label: t('tabs.roles') },
    ]

    const actions: ActionButton[] = [
        {
            text: t('users.list.quickPermissionButton'),
            icon: ShieldCheck,
            onClick: () => setOpenQuickPermission(true),
            variant: 'outline',
        },
        {
            text: t('users.list.createButton'),
            icon: PlusIcon,
            onClick: () => setOpenCreateUser(true),
            variant: 'default',
        },
    ]

    return (
        <TabLayout
            className="h-[calc(100vh-150px)] min-h-[500px] overflow-hidden"
            title={t('users.list.title')}
            linksTo={links}
            actions={actions}
        >
            <div className="flex flex-col gap-4 max-lg:gap-2 min-h-0 flex-1">
                <div className="flex gap-4 max-lg:flex-wrap max-lg:gap-2 shrink-0">
                    <BsSearchField
                        autoTrim
                        className="h-10 text-sm bg-background rounded-lg w-80 max-lg:w-full"
                        containerClassName="max-lg:w-full"
                        placeholder={t('users.list.searchPlaceholder')}
                        value={searchName}
                        onChange={e => setSearchName(e)}
                        onKeyDown={handleKeydown}
                    />
                    <FilterDropdown
                        placeholder={t('users.list.filters.regionPlaceholder')}
                        inputPlaceholder={t('users.list.filters.regionPlaceholder')}
                        options={regionOptions}
                        selectedValues={regionValues}
                        onChange={setRegionValues}
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                    />
                    <FilterDropdown
                        placeholder={t('users.list.filters.areaPlaceholder')}
                        inputPlaceholder={t('users.list.filters.areaPlaceholder')}
                        options={areaOptions}
                        selectedValues={areaValues}
                        onChange={setAreaValues}
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                        isDisabled={!regionValues || regionValues.length === 0}
                        disabledTooltip={t('common.filters.requireRegionFirst')}
                    />
                    <FilterDropdown
                        placeholder={t('users.list.filters.rolePlaceholder')}
                        inputPlaceholder={t('users.list.filters.rolePlaceholder')}
                        options={roleOptions}
                        selectedValues={roleValues}
                        onChange={setRoleValues}
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                    />
                    <FilterDropdown
                        placeholder={t('users.list.filters.statusPlaceholder')}
                        inputPlaceholder={t('users.list.filters.statusPlaceholder')}
                        options={statusOptions}
                        selectedValues={statusValues}
                        onChange={setStatusValues}
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                    />
                </div>
                <DataTable
                    data={data?.data || []}
                    columns={columns}
                    enableRowSelection={true}
                    enableSorting={true}
                    sorting={sorting}
                    setSorting={setSorting}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    isLoading={isLoading}
                    emptyString={t('common.emptyState')}
                    containerClassName="flex-1 overflow-y-auto min-h-0"
                />

                <div className="shrink-0">
                    <TableDataFooter
                        selectedCount={Object.keys(rowSelection).length}
                        onDelete={handleDeleteMultiple}
                        deleteDisabled={Object.keys(rowSelection).length === 0 || deleteMutation.isPending}
                        isLoading={deleteMutation.isPending}
                        totalCount={totalResults}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        totalPages={Math.ceil(totalResults / pageSize)}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={value => setPageSize(value || 10)}
                    />
                </div>
            </div>

            <AddUser
                title="Tạo người dùng mới"
                description="Thiết lập hồ sơ người dùng mới trong hệ thống."
                primaryActionLabel="Tạo"
                open={openCreateUser}
                onOpenChange={setOpenCreateUser}
            />

            <QuickPermissionDialog
                open={openQuickPermission}
                onOpenChange={setOpenQuickPermission}
                initialSelectedUserIds={Object.keys(rowSelection).filter(id => rowSelection[id])}
                onSuccess={() => setRowSelection({})}
            />

            <UserActionDialogs
                userAction={userAction}
                setUserAction={setUserAction}
                handleDelete={handleDelete}
                isDeletingUser={isDeletingUser}
            />
        </TabLayout>
    )
}

type UserActionDialogsProps = {
    userAction: { user: User; action: 'view' | 'edit' | 'delete' } | null
    setUserAction: (action: { user: User; action: 'view' | 'edit' | 'delete' } | null) => void
    handleDelete: () => void
    isDeletingUser: boolean
}

const UserActionDialogs = React.memo(function UserActionDialogs({
    userAction,
    setUserAction,
    handleDelete,
    isDeletingUser,
}: UserActionDialogsProps) {
    if (!userAction) {
        return null
    }

    const { action, user } = userAction
    const onOpenChange = () => setUserAction(null)
    return (
        <>
            <EditUser
                title="Chỉnh sửa thông tin người dùng"
                description="Cập nhật thông tin chi tiết của một người dùng hiện có."
                primaryActionLabel="Lưu"
                open={action === 'edit'}
                onOpenChange={onOpenChange}
                userId={user.id}
            />

            <DeleteUserConfirmDialog
                open={action === 'delete'}
                onOpenChange={onOpenChange}
                userEmail={user.email}
                userName={user.fullName}
                onConfirm={handleDelete}
                isLoading={isDeletingUser}
            />
            <UserDetail user={user} open={action === 'view'} onOpenChange={onOpenChange} />
        </>
    )
})
